import React from "react";
import styled from "styled-components";
import { Typography, Row, Col, Card, Divider, Button } from "antd";
import { TeamOutlined, TrophyFilled, MailOutlined, PhoneFilled, EnvironmentFilled, StarFilled, ArrowRightOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto 0 auto;
  padding: 48px 24px 40px 24px;
  background: linear-gradient(135deg, #fffbe7 0%, #ffe0b2 100%);
  border-radius: 32px;
  box-shadow: 0 8px 32px rgba(241,86,55,0.10);
  position: relative;
  overflow: hidden;
`;

const Slogan = styled(Title)`
  && {
    text-align: center;
    font-size: 2.2rem;
    color: #F15637;
    margin-bottom: 0;
    font-weight: 900;
    letter-spacing: 2px;
    background: linear-gradient(90deg, #F15637 30%, #FF9800 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Highlight = styled(Text)`
  color: #F15637;
  font-weight: bold;
`;

const FeatureCard = styled(Card)`
  border: none;
  box-shadow: 0 2px 12px rgba(241,86,55,0.10);
  border-radius: 20px;
  margin-bottom: 18px;
  background: #fff;
  transition: box-shadow 0.2s, transform 0.2s;
  min-height: 180px;
  &:hover {
    box-shadow: 0 8px 32px #ff980033;
    transform: translateY(-4px) scale(1.02);
  }
`;

const BannerImage = styled.img`
  width: 100%;
  max-height: 260px;
  object-fit: cover;
  border-radius: 22px;
  margin-bottom: 32px;
  box-shadow: 0 4px 24px rgba(241,86,55,0.10);
`;

const VideoWrapper = styled.div`
  margin: 32px 0;
  display: flex;
  justify-content: center;
  iframe, video {
    border-radius: 18px;
    box-shadow: 0 4px 24px rgba(241,86,55,0.10);
    width: 100%;
    max-width: 540px;
    height: 320px;
  }
`;

const StatBox = styled.div`
  background: linear-gradient(135deg, #fffbe7 60%, #ffe0b2 100%);
  border-radius: 18px;
  box-shadow: 0 2px 12px #ff980022;
  padding: 36px 0 28px 0;
  text-align: center;
  margin-bottom: 24px;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 8px 32px #ff980033;
  }
`;

const PartnerLogo = styled.img`
  height: 48px;
  margin: 0 18px 12px 0;
  filter: grayscale(0.2);
  opacity: 0.85;
  transition: filter 0.2s, opacity 0.2s;
  &:hover {
    filter: grayscale(0);
    opacity: 1;
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin: 48px 0 24px 0;
`;

const AboutUs = () => (
  <AboutContainer>
    <BannerImage src="/images/banner1.jpg" alt="MIXI Fitness Banner" />
    <Slogan level={1}>Nâng tầm sức khỏe - Kết nối đam mê</Slogan>
    <Paragraph style={{ fontSize: 18, textAlign: "center", margin: "18px 0 32px 0", color: "#444" }}>
      MIXI Fitness là <Highlight>nền tảng trung gian uy tín</Highlight> kết nối người dùng với các trung tâm thể hình, phòng gym, studio hàng đầu Việt Nam.<br />
      <span style={{ color: "#388e3c" }}>Sứ mệnh của chúng tôi là giúp bạn dễ dàng khám phá, so sánh và đặt dịch vụ fitness phù hợp với lối sống & mục tiêu cá nhân.</span>
    </Paragraph>

    {/* Thống kê nổi bật */}
    <Row gutter={[24, 24]} justify="center" style={{ marginBottom: 32 }}>
      <Col xs={24} sm={8}>
        <StatBox>
          <TrophyFilled style={{ fontSize: 40, color: "#FF9800", marginBottom: 8 }} />
          <Title level={3} style={{ color: "#F15637", margin: 0, fontWeight: 800 }}>300+</Title>
          <Text style={{ fontSize: 17 }}>Phòng gym đối tác</Text>
        </StatBox>
      </Col>
      <Col xs={24} sm={8}>
        <StatBox>
          <StarFilled style={{ fontSize: 40, color: "#388e3c", marginBottom: 8 }} />
          <Title level={3} style={{ color: "#388e3c", margin: 0, fontWeight: 800 }}>10.000+</Title>
          <Text style={{ fontSize: 17 }}>Người dùng hài lòng</Text>
        </StatBox>
      </Col>
      <Col xs={24} sm={8}>
        <StatBox>
          <TeamOutlined style={{ fontSize: 40, color: "#d32f2f", marginBottom: 8 }} />
          <Title level={3} style={{ color: "#d32f2f", margin: 0, fontWeight: 800 }}>500.000+</Title>
          <Text style={{ fontSize: 17 }}>Lượt đặt gói tập</Text>
        </StatBox>
      </Col>
    </Row>

    {/* Video giới thiệu */}
    <VideoWrapper>
      <iframe
        src="https://www.youtube.com/embed/I99b2KnXSNw"
        title="Giới thiệu MIXI Fitness"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </VideoWrapper>

    {/* Lý do chọn & Tầm nhìn */}
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12}>
        <FeatureCard>
          <Title level={4} style={{ color: "#F15637" }}>Tại sao chọn chúng tôi?</Title>
          <ul style={{ paddingLeft: 20, fontSize: 16, color: "#444", marginBottom: 0 }}>
            <li>
              <Highlight>Danh sách đa dạng:</Highlight> Truy cập hàng trăm phòng gym, studio, trung tâm fitness chất lượng.
            </li>
            <li>
              <Highlight>Đánh giá xác thực:</Highlight> Xem nhận xét thực tế từ cộng đồng tập luyện.
            </li>
            <li>
              <Highlight>Đặt lịch dễ dàng:</Highlight> Đặt chỗ hoặc mua gói tập chỉ với vài thao tác.
            </li>
            <li>
              <Highlight>Tìm kiếm cá nhân hóa:</Highlight> Lọc theo vị trí, giá, tiện ích, mục tiêu,...
            </li>
          </ul>
        </FeatureCard>
      </Col>
      <Col xs={24} md={12}>
        <FeatureCard>
          <Title level={4} style={{ color: "#388e3c" }}>Tầm nhìn của chúng tôi</Title>
          <Paragraph style={{ color: "#444" }}>
            Chúng tôi tin rằng ai cũng xứng đáng tiếp cận dịch vụ fitness chất lượng. MIXI Fitness giúp bạn chủ động lựa chọn, tiết kiệm thời gian và duy trì động lực trên hành trình sức khỏe.
          </Paragraph>
          <Paragraph style={{ color: "#388e3c", fontWeight: 600 }}>
            <Highlight>Tham gia cùng chúng tôi</Highlight> để trải nghiệm phong cách fitness hiện đại, tiện lợi!
          </Paragraph>
        </FeatureCard>
      </Col>
    </Row>

    

    {/* Sứ mệnh & Giá trị */}
    <Divider style={{ margin: "40px 0 32px 0", borderColor: "#ffe0b2" }}>
      <Title level={4} style={{ color: "#F15637", margin: 0 }}>Sứ mệnh & Giá trị</Title>
    </Divider>
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12}>
        <FeatureCard>
          <Title level={4}>Sứ mệnh của MIXI Fitness</Title>
          <Paragraph>
            <Highlight>Sứ mệnh</Highlight> của chúng tôi là tạo ra một hệ sinh thái fitness hiện đại, nơi mọi người đều có thể tiếp cận, trải nghiệm và duy trì lối sống lành mạnh một cách dễ dàng, minh bạch và tiết kiệm nhất.
          </Paragraph>
        </FeatureCard>
      </Col>
      <Col xs={24} md={12}>
        <FeatureCard>
          <Title level={4}>Giá trị cốt lõi</Title>
          <ul style={{ paddingLeft: 20, fontSize: 16, marginBottom: 0 }}>
            <li><Highlight>Uy tín:</Highlight> Cam kết minh bạch, bảo vệ quyền lợi người dùng và đối tác.</li>
            <li><Highlight>Tiện lợi:</Highlight> Đặt dịch vụ nhanh chóng, thanh toán an toàn, hỗ trợ tận tâm.</li>
            <li><Highlight>Kết nối:</Highlight> Xây dựng cộng đồng tập luyện năng động, truyền cảm hứng.</li>
          </ul>
        </FeatureCard>
      </Col>
    </Row>

    {/* Đội ngũ & Thành tựu */}
    <Divider style={{ margin: "40px 0 32px 0", borderColor: "#ffe0b2" }}>
      <Title level={4} style={{ color: "#388e3c", margin: 0 }}>Đội ngũ & Thành tựu</Title>
    </Divider>
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12}>
        <FeatureCard>
          <Title level={4}>Đội ngũ sáng lập</Title>
          <Paragraph>
            Được sáng lập bởi những người trẻ đam mê công nghệ và thể thao, MIXI Fitness luôn đổi mới để mang lại trải nghiệm tốt nhất cho khách hàng.
          </Paragraph>
        </FeatureCard>
      </Col>
      <Col xs={24} md={12}>
        <FeatureCard>
          <Title level={4}>Đối tác & Thành tựu</Title>
          <Paragraph>
            Hơn <Highlight>300+ phòng gym</Highlight> và <Highlight>10.000+ người dùng</Highlight> đã tin tưởng sử dụng MIXI Fitness. Chúng tôi tự hào là cầu nối giữa cộng đồng fitness và các trung tâm uy tín trên toàn quốc.
          </Paragraph>
        </FeatureCard>
      </Col>
    </Row>

    {/* CTA */}
    <CTASection>
      <Button
        type="primary"
        size="large"
        icon={<ArrowRightOutlined />}
        style={{
          background: "linear-gradient(90deg, #FF9800 60%, #F15637 100%)",
          border: "none",
          borderRadius: 24,
          fontWeight: 700,
          fontSize: 20,
          padding: "12px 48px",
          boxShadow: "0 4px 24px #ff980033"
        }}
        href="/"
      >
        Khám phá ngay MIXI Fitness
      </Button>
    </CTASection>

    {/* Liên hệ */}
    <Divider style={{ margin: "40px 0 32px 0", borderColor: "#ffe0b2" }}>
      <Title level={4} style={{ color: "#F15637", margin: 0 }}>Liên hệ</Title>
    </Divider>
    <Row gutter={[24, 24]}>
      <Col xs={24}>
        <FeatureCard>
          <Title level={4} style={{ color: "#F15637" }}>Liên hệ với chúng tôi</Title>
          <Paragraph style={{ fontSize: 16 }}>
            <MailOutlined style={{ color: "#F15637", marginRight: 8 }} />
            Email: <a href="mailto:support@mixifitness.vn">support@mixifitness.vn</a><br />
            <PhoneFilled style={{ color: "#388e3c", marginRight: 8 }} />
            Hotline: <a href="tel:0123456789">0123 456 789</a><br />
            <EnvironmentFilled style={{ color: "#FF9800", marginRight: 8 }} />
            Địa chỉ: 123 Nguyễn Văn Linh, Đà Nẵng
          </Paragraph>
        </FeatureCard>
      </Col>
    </Row>
  </AboutContainer>
);

export default AboutUs;